/**
 * Whether the environment's subscription has the Standard public IP addresses the servers asked for, in the chosen region. Read from the
 * Infrastructure store, so every place that shows the answer (the RDS and DB questions, "To continue", the Next button) asks the same
 * question and shares one answer. It asks only while a public IP is wanted (`publicIpCount` above 0) and the environment and region are
 * chosen: with every answer No, the endpoint is never called.
 */
import { useInfrastructureWizard, publicIpCount } from '@/stores/infrastructureWizard'
import { usePublicIpQuotaQuery } from '@/hooks/useInfra'
import { publicIpProblems, publicIpQuotaState, type PublicIpQuotaState } from '@/utils/publicIpQuota'

export interface PublicIpQuotaResult {
  /** How many public IPs the servers want in all (one per RDS VM of a Yes, plus one for the DB server's Yes). */
  count: number
  state: PublicIpQuotaState
  /** What to tell the user: the backend's message as given, or the portal's own when the request failed. Empty when there is nothing to say. */
  message: string
  /** Ask again (the quota may have been raised, or the lookup may have failed for a moment). */
  recheck: () => void
  /** True while the check is being asked, the first time or again. */
  isChecking: boolean
  /** Why the Servers step cannot continue. Empty when no public IP is wanted or the quota is there. Never silent. */
  problems: string[]
}

/** `enabled` is false where the Infrastructure flow is not in use, so nothing is asked of a store that holds answers nobody is looking at. */
export function usePublicIpQuota(enabled = true): PublicIpQuotaResult {
  const product = useInfrastructureWizard((s) => s.product)
  const level = useInfrastructureWizard((s) => s.level)
  const rdsServer = useInfrastructureWizard((s) => s.rdsServer)
  const dbServer = useInfrastructureWizard((s) => s.dbServer)
  const environmentId = useInfrastructureWizard((s) => s.environment?.id ?? '')
  const regionId = useInfrastructureWizard((s) => s.region?.id ?? '')

  const count = publicIpCount({ product, level, rdsServer, dbServer })
  const wanted = enabled && count > 0 && environmentId !== '' && regionId !== ''
  const query = usePublicIpQuotaQuery(wanted ? { environmentId, regionId, count } : null)

  const state = publicIpQuotaState({ wanted, isError: query.isError, data: query.data })
  return {
    count,
    state,
    message: state.kind === 'short' || state.kind === 'unreachable' ? state.message : '',
    recheck: () => {
      if (wanted) void query.refetch()
    },
    isChecking: wanted && query.isFetching,
    problems: publicIpProblems(state),
  }
}
